/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { ethers, JsonRpcSigner } from "ethers";
import { AppState } from "../types/types";
import { CONTROLLER_ADDRESS, TOKENS } from "../config/configContracts";
import { Contract_abi__factory } from "../config/factories/Contract_abi__factory";
import TOKEN_NAU_ABI from "../config/NAU_ABI.json";
import { calculateAmounts } from "./calculateAmounts";

const handleTransformX = async (
  signer: JsonRpcSigner,
  state: AppState,
  setState: React.Dispatch<React.SetStateAction<AppState>>,
  ratioX1: number,
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
  tokenBalance: string,
  setModalTx: React.Dispatch<
    React.SetStateAction<{
      txStatus: "pending" | "confirmed" | "failed" | null;
      txHash: string | null;
    }>
  >
): Promise<void> => {
  try {
    // Initialize UI state for pending transaction
    setState((prev) => ({
      ...prev,
      loading: true,
      txStatus: "pending",
      error: null,
    }));
    setModalTx({ txStatus: "pending", txHash: null });
    setShowModal(true);

    // Ensure contract address is configured
    if (!CONTROLLER_ADDRESS) return;

    // Prepare contract interfaces
    const signerAddress = await signer.getAddress();
    const contract = Contract_abi__factory.connect(CONTROLLER_ADDRESS, signer);

    // Parse balances to BigInt for comparison
    const userBalance = ethers.parseUnits(tokenBalance, 18);
    const burnAmount = ethers.parseUnits(state.amountNAU.toString(), 18);

    if (userBalance < burnAmount) {
      throw new Error("Insufficient balance to burn X.");
    }

    // Calculate how much NAUY and NAUN to receive
    const amounts = await calculateAmounts(state.amountNAU, ratioX1);
    if (!amounts) throw new Error("Error calculating amounts.");
    const { amountNAUY, amountNAUN } = amounts;

    // Convert ratio to on-chain integer (basis points)
    const ratio1Bp = BigInt(Math.round(ratioX1 * 10000));
    const ratio2Bp = BigInt(10000) - ratio1Bp;

    // Verify network (should be Base chain)
    const network = await signer.provider!.getNetwork();
    if (network.chainId !== BigInt(8453)) {
      throw new Error("You are not connected to Base network.");
    }

    // Approve the contract to burn the user's tokens
    const tokenContract = new ethers.Contract(
      TOKENS.NAU,
      TOKEN_NAU_ABI,
      signer
    );
    const approveTx = await tokenContract.approve(
      CONTROLLER_ADDRESS,
      burnAmount
    );
    await approveTx.wait(1);

    // Execute transformX on the contract
    const burnTx = await contract.transformX(
      burnAmount,
      ratio1Bp,
      ratio2Bp,
      {}
    );
    await burnTx.wait(1);

    // Update UI state on confirmation
    setState((prev) => ({
      ...prev,
      txHash: burnTx.hash,
      txStatus: "confirmed",
      loading: false,
    }));
    setModalTx({ txStatus: "confirmed", txHash: burnTx.hash });

    // Determine user wallet type for backend logging
    const providerObj = signer.provider as any;
    let walletName = "Unknown";
    if (providerObj.connection?.url?.includes("walletconnect"))
      walletName = "WalletConnect";
    else if (providerObj.isMetaMask) walletName = "MetaMask";
    else if (providerObj.isCoinbaseWallet) walletName = "Coinbase Wallet";
    else if (providerObj.isTrust) walletName = "Trust Wallet";
    else if (providerObj.isBraveWallet) walletName = "Brave Wallet";
    else if (providerObj.isPhantom) walletName = "Phantom Wallet";
    else if (providerObj.isExodus) walletName = "Exodus Wallet";
    else if (providerObj.isRainbow) walletName = "Rainbow Wallet";
    else if (providerObj.isArgent) walletName = "Argent Wallet";
    else if (providerObj.isZerion) walletName = "Zerion Wallet";
    else if (providerObj.isOKXWallet) walletName = "OKX Wallet";
    else if (providerObj.isImToken) walletName = "imToken Wallet";
    else if (providerObj.isTokenPocket) walletName = "TokenPocket Wallet";
    else if (providerObj.isBitKeep) walletName = "BitKeep Wallet";
    else if (providerObj.isSafe) walletName = "Safe Wallet";
    else if (providerObj.isMathWallet) walletName = "Math Wallet";
    else if (providerObj.isDcentWallet) walletName = "D'CENT Wallet";
    else if (providerObj.isHuobiWallet) walletName = "Huobi Wallet";
    else if (providerObj.isLedgerLive) walletName = "Ledger Live";
    else if (providerObj.isTorus) walletName = "Torus Wallet";
    else if (providerObj.isEnkrypt) walletName = "Enkrypt Wallet";
    else if (providerObj.isFrame) walletName = "Frame Wallet";
    else if (providerObj.isOpera) walletName = "Opera Wallet";
    else if (providerObj.isStatus) walletName = "Status Wallet";
    else if (providerObj.isTrustWallet) walletName = "Trust Wallet";
    // Log the burn event to backend (best-effort)
    try {
      await axios.post(
        "https://site--ucwaves-p1-backend-deployment--r9qw5p2frw6c.code.run/burnNAU",
        {
          userAddress: signerAddress,
          NAUBurn: Number(ethers.formatUnits(burnAmount, 18)),
          NAUYMint: Number(amountNAUY),
          NAUNMint: Number(amountNAUN),
          txHash: burnTx.hash,
          walletName,
        }
      );
    } catch {
      // Optional: handle backend logging failure silently
    }
  } catch (err) {
    // Handle any errors: update UI state and close modal after delay
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    setState((prev) => ({
      ...prev,
      txStatus: "failed",
      loading: false,
      error: message,
    }));
    setModalTx({ txStatus: "failed", txHash: null });
    setTimeout(() => setShowModal(false), 2000);
  }
};

export default handleTransformX;
